/**
 * Client Rate Card
 * -----------------
 * Per-client comparison of every item that client has ever been billed:
 *   • Most recent rate charged (with invoice # / date)
 *   • Current master price (B2B or B2C, matching the client's invoice type)
 *   • Drift % vs master → helps spot pricing that has silently gone stale
 *
 * Opened from the Clients page and the Ledger drawer via
 *   /clients/rate-card?name=X&gstin=Y
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Search, TrendingDown, TrendingUp, Minus, Loader2 } from "lucide-react";

const driftTone = (pct) => {
    if (pct === null || pct === undefined) return { cls: "text-slate-400", Icon: Minus };
    if (pct >= 5)  return { cls: "text-emerald-700 bg-emerald-50", Icon: TrendingUp };
    if (pct <= -5) return { cls: "text-red-700 bg-red-50",         Icon: TrendingDown };
    return { cls: "text-slate-500", Icon: Minus };
};

export default function ClientRateCardPage() {
    const [sp] = useSearchParams();
    const name  = sp.get("name")  || "";
    const gstin = sp.get("gstin") || "";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!name) { setLoading(false); return; }
        setLoading(true);
        api.get(`/clients/${encodeURIComponent(name)}/rate-card`, { params: { gstin } })
           .then((r) => setData(r.data))
           .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load rate card"))
           .finally(() => setLoading(false));
    }, [name, gstin]);

    const filtered = useMemo(() => {
        const rows = data?.rows || [];
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter((r) => r.name.toLowerCase().includes(s)
                             ||  (r.category || "").toLowerCase().includes(s)
                             ||  (r.hsn || "").toLowerCase().includes(s));
    }, [data, q]);

    if (!name) {
        return (
            <div className="p-6" data-testid="rate-card-empty">
                <div className="hdb-card p-8 text-center text-slate-500">
                    Open the rate card from a client row (Clients → Rate card).
                </div>
            </div>
        );
    }

    return (
        <div data-testid="client-rate-card-page">
            <div className="mb-5">
                <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                    <ArrowLeft size={12}/> Back to Clients
                </Link>
                <div className="mt-2 flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl hdb-heading font-black text-slate-900">Rate card</h1>
                        <p className="text-slate-500 mt-1 text-sm">
                            <b className="text-slate-900">{name}</b>
                            {gstin && <span className="hdb-mono ml-2 text-xs">· {gstin}</span>}
                            {data && (
                                <span className="ml-2 text-xs">
                                    · {data.invoice_type} rates ·
                                    scanned {data.invoices_scanned} invoice{data.invoices_scanned !== 1 ? "s" : ""}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400"/>
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                               placeholder="Search item / category / HSN"
                               className="hdb-input pl-8 w-64"
                               data-testid="rate-card-search"/>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="hdb-card p-10 text-center text-slate-400" data-testid="rate-card-loading">
                    <Loader2 className="inline animate-spin mr-2" size={16}/> Loading rate card…
                </div>
            ) : (!data || !data.rows.length) ? (
                <div className="hdb-card p-10 text-center text-slate-500" data-testid="rate-card-empty-rows">
                    No invoices yet for this client — nothing to compare.
                </div>
            ) : (
                <div className="hdb-card overflow-hidden">
                    <table className="hdb-table w-full text-sm">
                        <thead>
                            <tr>
                                <th className="text-left">Item</th>
                                <th className="text-left">Category</th>
                                <th className="text-left hdb-mono">HSN</th>
                                <th className="text-right">Times billed</th>
                                <th className="text-right">Last rate</th>
                                <th className="text-right">Master rate</th>
                                <th className="text-right">Drift</th>
                                <th className="text-left">Last on</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r, i) => {
                                const t = driftTone(r.master_rate ? r.drift_pct : null);
                                const Icon = t.Icon;
                                return (
                                    <tr key={i} data-testid={`rate-card-row-${i}`}>
                                        <td>
                                            <div className="font-semibold text-slate-900">{r.name}</div>
                                            {r.distinct_rates?.length > 1 && (
                                                <div className="text-[10px] text-slate-400 hdb-mono">
                                                    {r.distinct_rates.length} distinct rates seen
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-slate-600">{r.category || "—"}</td>
                                        <td className="hdb-mono text-slate-500">{r.hsn || "—"}</td>
                                        <td className="hdb-num hdb-num-right">{r.times_billed}</td>
                                        <td className="hdb-num hdb-num-right font-bold">{fmt(r.last_rate)}</td>
                                        <td className="hdb-num hdb-num-right text-slate-600">
                                            {r.master_rate ? fmt(r.master_rate) : <span className="text-slate-300">n/a</span>}
                                        </td>
                                        <td className="hdb-num hdb-num-right">
                                            {r.master_rate ? (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold ${t.cls}`}>
                                                    <Icon size={11}/>
                                                    {r.drift_pct > 0 ? "+" : ""}{r.drift_pct}%
                                                </span>
                                            ) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="hdb-mono text-xs text-slate-500">
                                            {r.last_invoice_number}
                                            <div className="text-[10px] text-slate-400">{r.last_invoice_date}</div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-8 text-slate-400">
                                    No matches for "{q}".
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
